/**
 * nested-step-keys — the SINGLE place this validator writes down which step
 * properties hold a nested array of ActionFlow steps.
 *
 * Why this file exists
 * --------------------
 * The same fact was hand-typed in four places and two of them disagreed:
 *
 *   - `stage-actionflow-deep.ts` `collectReactiveFacts` (13 keys + `switch` cases)
 *   - `stage-actionflow-deep.ts` `walkStepsDeep`        (13 keys + `switch` cases)
 *   - `stage-contract.ts`        `gatherSetStateTargets` (3 keys, no cases)
 *   - `stage-contract.ts`        `checkSetStateTargets`  (3 keys, no cases)
 *
 * The short lists are not a scoping decision, they are drift: `contract-
 * setstate-target` exists to report a setState onto undeclared state, and its
 * own severity-table reasoning is "write is lost, never reactive". A step
 * inside `tryCatch.try` is exactly as lost as one inside `then` — the rule
 * simply could not see it.
 *
 * Measured 2026-08-26 against the repo's own flow corpus (3187 `flow.json` /
 * `flow.cogo.json` files): 584 files carry at least one `setState` step that
 * the 3-key walkers cannot reach, 4612 such steps in total. A live block
 * generated the same day wrapped its ENTIRE flow body in a single `tryCatch`,
 * so every one of its writes was invisible to this stage.
 *
 * The blindness cuts both ways, which is why both call sites matter:
 *   - `checkSetStateTargets` under-reports (a dead write is never flagged).
 *   - `collectSetStateTargets` under-collects, and its output is the
 *     "is this path ever written?" evidence for `contract-unresolved-ui-symbol`
 *     — so a UI binding reading a value that IS set inside a `tryCatch` was
 *     reported as never declared anywhere. A false positive on correct output.
 *
 * The rule for this file: **members are declared once.** A consumer walks
 * nested steps by calling `forEachNestedStepArray`; it does not retype the
 * list. `__tests__/nested-step-keys.test.ts` asserts that no consumer holds an
 * independent copy.
 */

/**
 * Step properties whose value, when an array, is an ordered list of nested
 * ActionFlow steps.
 *
 * Derived from the runtime's own control-flow surface: `then`/`else`
 * (conditional), `steps` (loop / parallel / stage), `try`/`catch`/`finally`
 * (tryCatch), `default` (switch fallthrough), `onEnter`/`onSuccess`/`onError`/
 * `onFinally` (lifecycle hooks), `compensate` + `stages` (saga).
 *
 * `switch` cases live under an OBJECT (`step.cases`), not an array property,
 * so they are handled separately by `forEachNestedStepArray` rather than
 * listed here.
 */
export const NESTED_STEP_ARRAY_KEYS = [
  "then",
  "else",
  "steps",
  "try",
  "catch",
  "finally",
  "default",
  "onEnter",
  "onSuccess",
  "onError",
  "onFinally",
  "compensate",
  "stages",
] as const

export type NestedStepArrayKey = (typeof NESTED_STEP_ARRAY_KEYS)[number]

/**
 * Invoke `visit` once per nested step array reachable from `step`, including
 * every branch of a `switch` step's `cases` object.
 *
 * This is the ONLY supported way to descend an ActionFlow step tree in this
 * package. Walking `step.then` / `step.steps` by hand is what produced the
 * drift documented above.
 */
export function forEachNestedStepArray(
  step: Record<string, unknown>,
  visit: (steps: Record<string, unknown>[]) => void,
): void {
  for (const key of NESTED_STEP_ARRAY_KEYS) {
    const nested = step[key]
    if (Array.isArray(nested)) visit(nested as Record<string, unknown>[])
  }
  const cases = step.cases
  if (cases && typeof cases === "object" && !Array.isArray(cases)) {
    for (const branch of Object.values(cases as Record<string, unknown>)) {
      if (Array.isArray(branch)) visit(branch as Record<string, unknown>[])
    }
  }
}
